import { AlertTriangle } from "lucide-react";
import type { BuyerImportListingInterestDto } from "@/api/buyerSmartImport";
import { Input, Skeleton, SubTable, type SubTableColumn } from "@/design-system/components/ui";
import { smartImportCopy } from "./smartImportCopy";
import { formatGeographyList, parseCommaList, parseGeographyList } from "./smartImportReview";

/**
 * What each listing in the file says about a buyer who enquired about it (D8).
 *
 * <p>🔴 <b>The switch is an opt-OUT and starts ON</b> — see
 * `reviewFromAnalysis`. A buyer who enquired about a Phoenix HVAC business has
 * told us their industry and their geography without being asked, and that is
 * most of what makes an imported row worth more than a name and an email. A
 * default of off would throw it away on every import, silently.</p>
 *
 * <p>Every inferred value is EDITABLE, because this is a model reading a
 * listing title: "Turnkey Restaurant Group — Phoenix Metro" is unambiguous and
 * "Established Route Business" is not, and the operator is the only party who
 * can tell the two apart. The empty state and the failure state are deliberately
 * different sentences: "no listings in the mapped columns" is a fact about the
 * file the operator can act on, and a model failure is not.</p>
 */

export interface SmartImportInterestsTableProps {
  /** The table as the operator has left it. */
  interests: readonly BuyerImportListingInterestDto[];
  /** True while `inferInterests` is on. */
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  onChange: (interests: BuyerImportListingInterestDto[]) => void;
  /** True while the interests call is in flight. */
  loading?: boolean;
  /** Set when the model failed; the import still runs without interests (D19). */
  aiFailureMessage?: string | null;
  disabled?: boolean;
}

export function SmartImportInterestsTable({
  interests,
  enabled,
  onToggle,
  onChange,
  loading,
  aiFailureMessage,
  disabled,
}: SmartImportInterestsTableProps) {
  /*
    🔴 The two lists do NOT parse the same way, and a shared parser here was a
    silent corruption: "Phoenix, AZ" is ONE place, so splitting a geography on
    its comma writes a city and a bare state code as two separate targets — and
    because `["Phoenix", "AZ"].join(", ")` renders identically to the value that
    went in, the operator sees nothing change. The server draws exactly this
    line by name (`Transforms.CsvList` splits on `,` for every target except a
    geography); this is the same line at the one control that edits the list.
  */
  const change = (
    key: string,
    field: "industries" | "geographies",
    raw: string,
  ): void => {
    const parse = field === "geographies" ? parseGeographyList : parseCommaList;
    onChange(
      interests.map((listing) =>
        listing.key === key ? { ...listing, [field]: parse(raw) } : listing,
      ),
    );
  };

  const columns: SubTableColumn<BuyerImportListingInterestDto>[] = [
    {
      key: "listing",
      header: "Listing",
      render: (l) => (
        <div className="flex flex-col gap-space-1">
          <span className="font-medium text-onyx break-words">{l.title || l.key}</span>
          {l.title && l.title !== l.key && (
            <span className="text-caption text-gray-mid">{l.key}</span>
          )}
        </div>
      ),
    },
    {
      key: "industries",
      header: "Industries",
      render: (l) => (
        <Input
          aria-label={`Industries, for ${l.title || l.key}`}
          data-testid={`interest-industries-${l.key}`}
          className="text-small"
          disabled={disabled}
          placeholder="None"
          value={l.industries.join(", ")}
          onChange={(e) => change(l.key, "industries", e.target.value)}
        />
      ),
    },
    {
      key: "geographies",
      // Says which separator, because it is not the one the box beside it uses.
      header: "Geographies (separate with ;)",
      render: (l) => (
        <Input
          aria-label={`Geographies, for ${l.title || l.key}`}
          data-testid={`interest-geographies-${l.key}`}
          className="text-small"
          disabled={disabled}
          placeholder="None"
          value={formatGeographyList(l.geographies)}
          onChange={(e) => change(l.key, "geographies", e.target.value)}
        />
      ),
    },
  ];

  return (
    <section aria-label={smartImportCopy.interestsTitle} data-testid="smart-import-interests">
      <h3 className="font-display font-semibold text-label uppercase tracking-label text-gold-shadow mb-space-2">
        {smartImportCopy.interestsTitle}
      </h3>

      <label className="flex items-start gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          data-testid="smart-import-interests-switch"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-[3px]"
        />
        <span>
          {smartImportCopy.interestsSwitch}
          <span className="block text-caption text-gray-mid">
            {smartImportCopy.interestsHint}
          </span>
        </span>
      </label>

      {enabled && aiFailureMessage && (
        <p
          className="mt-space-2 flex items-start gap-space-2 text-small text-status-warning-ink"
          data-testid="smart-import-interests-failure"
        >
          <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <span>{aiFailureMessage}</span>
        </p>
      )}

      {enabled &&
        (loading ? (
          <div className="mt-space-3" data-testid="smart-import-interests-loading">
            <p className="text-small text-gray-mid">{smartImportCopy.interestsLoading}</p>
            <Skeleton className="mt-space-2 h-24 w-full" />
          </div>
        ) : (
          <div className="mt-space-3">
            <SubTable
              caption={smartImportCopy.interestsTitle}
              data-testid="smart-import-interests-table"
              columns={columns}
              rows={[...interests]}
              rowKey={(l) => l.key}
              rowTestId={(l) => `interest-row-${l.key}`}
              empty={{ message: smartImportCopy.interestsEmpty }}
            />
          </div>
        ))}
    </section>
  );
}
